import { useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';
import { formatDate } from '@/shared/utils/format';
import type { ReviewsReportData } from '../types';
import { funnelRows, MIN_SENDS_TO_COMPARE, reviewsChecks, reviewsTake } from '../utils/reviewsReport';
import styles from './TaskPanels.module.css';

/**
 * Betty's weekly reviews report, drawn from the figures of the reads it cites: her take, (1) each
 * step a patient takes from visit to Google review, this week or last, (2) quick checks, then the
 * fine print and her full written report folded away. Reporting only: nothing here changes the
 * text program.
 */
export function BettyReviewsReport({
  report,
  agentName,
  fullReport,
}: {
  report: ReviewsReportData;
  agentName: string;
  fullReport: ReactNode | null;
}) {
  const [week, setWeek] = useState<'now' | 'previous'>('now');
  const [notesOpen, setNotesOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const take = reviewsTake(report, agentName);
  const rows = funnelRows(report, week);
  const checks = reviewsChecks(report);
  const range = report.range
    ? `${formatDate(new Date(report.range.start), 'MMM d')} to ${formatDate(new Date(new Date(report.range.end).getTime() - 1), 'MMM d')}`
    : null;

  return (
    <div className={styles.stack}>
      <section className={styles.take} aria-label={`${agentName}'s take`}>
        <div className={styles.takeKicker}>{agentName}&apos;s take</div>
        <p className={styles.takeText}>{take.sentences}</p>
        <div className={styles.tiles}>
          {take.tiles.map((tile) => (
            <div key={tile.label} className={styles.tile}>
              <div className={styles.tileLabel}>{tile.label}</div>
              <div className={styles.tileValue}>{tile.value}</div>
              <div className={styles.tileNote}>{tile.note}</div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section} aria-label="From visit to Google review">
        <div className={styles.sectionHead}>
          <StepTitle step={1} title="From visit to Google review" sub="Each step a patient takes after their visit." />
          {report.previous && (
            <div className={styles.segmented} role="group" aria-label="Week">
              <button type="button" className={clsx(styles.segment, week === 'now' && styles.segmentOn)} aria-pressed={week === 'now'} onClick={() => setWeek('now')}>
                This week
              </button>
              <button type="button" className={clsx(styles.segment, week === 'previous' && styles.segmentOn)} aria-pressed={week === 'previous'} onClick={() => setWeek('previous')}>
                Last week
              </button>
            </div>
          )}
        </div>
        <div className={styles.box}>
          <div className={styles.funnel}>
            {rows.map((row) => (
              <div key={row.label} className={styles.funnelRow}>
                <div className={styles.funnelLabel}>{row.label}</div>
                <progress className={styles.bar} max={100} value={row.width} aria-hidden="true" />
                <div className={styles.funnelValue}>
                  <strong>{row.value}</strong>
                  {row.note && <span className={styles.funnelNote}> {row.note}</span>}
                </div>
              </div>
            ))}
          </div>
          {report.now.sent < MIN_SENDS_TO_COMPARE && (
            <div className={styles.boxFoot}>
              Too few texts to compare rates week to week yet. Rates mean more once {MIN_SENDS_TO_COMPARE} texts have gone out.
            </div>
          )}
        </div>
      </section>

      {checks.length > 0 && (
        <section className={styles.section} aria-label="Everything else this week">
          <StepTitle step={2} title="Everything else this week" sub="Quick checks. Green means nothing needs you." />
          <div className={styles.checks}>
            {checks.map((check) => (
              <div key={check.title} className={styles.check}>
                <span className={clsx(styles.dot, check.tone === 'ok' && styles.dotOk, check.tone === 'attention' && styles.dotAttention)} aria-hidden="true" />
                <div>
                  <div className={styles.checkTitle}>{check.title}</div>
                  <div className={styles.checkBody}>{check.body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className={styles.fold}>
        <button type="button" className={styles.foldHeader} aria-expanded={notesOpen} onClick={() => setNotesOpen((v) => !v)}>
          <span className={styles.foldTitle}>
            How to read these numbers <span className={styles.optional}>Optional</span>
          </span>
          <ChevronDown size={18} aria-hidden="true" className={clsx(styles.chevron, notesOpen && styles.chevronOpen)} />
        </button>
        {notesOpen && (
          <div className={styles.foldBody}>
            {range && (
              <p>
                <strong>Week.</strong> {range}, compared with the week before.
              </p>
            )}
            <p>
              <strong>New Google reviews are an estimate.</strong> Google doesn&apos;t say who wrote a review, so {agentName} matches new reviews to patients who got a text, by name and time.
            </p>
            <p>
              <strong>&ldquo;Approved their review draft&rdquo;</strong> is the patient&apos;s step: they agreed to the review wording {agentName} suggested before opening Google.
            </p>
            {report.testMode !== null && (
              <p>
                <strong>Test mode is {report.testMode ? 'on' : 'off'}.</strong> {report.testMode ? 'Texts go only to the test number, not to patients.' : 'Texts go to real patients.'}
              </p>
            )}
            {fullReport && (
              <>
                <button type="button" className={styles.linkButton} aria-expanded={fullOpen} onClick={() => setFullOpen((v) => !v)}>
                  {fullOpen ? `Hide ${agentName}'s full report` : `Read ${agentName}'s full report`}
                </button>
                {fullOpen && <div className={styles.fullReport}>{fullReport}</div>}
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export function StepTitle({ step, title, sub }: { step: number; title: string; sub?: string }) {
  return (
    <div className={styles.stepTitleRow}>
      <span className={styles.stepNumber} aria-hidden="true">
        {step}
      </span>
      <div>
        <h3 className={styles.stepTitle}>{title}</h3>
        {sub && <p className={styles.stepSub}>{sub}</p>}
      </div>
    </div>
  );
}
